/**
 * Condividi (Andrea 2026-10-08): formazione o risultato via WhatsApp,
 * Telegram, Instagram… Sul telefono si apre il foglio di condivisione del
 * sistema con l'immagine (Web Share API: compaiono tutte le app, Instagram
 * compresa). Dove non c'e' (desktop, browser vecchi) si apre una finestra
 * nostra con: anteprima, salva immagine, WhatsApp, Telegram, copia testo.
 */

import { useState } from "react";
import { tr } from "../../i18n";

export interface ContenutoCondivisione {
  blob: Blob;
  nomeFile: string;
  titolo: string;
  testo: string;
}

function puoCondividereFile(file: File): boolean {
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  return typeof nav.share === "function" && !!nav.canShare?.({ files: [file] });
}

/** Prova il foglio di sistema; true se gestito, false se serve la finestra nostra. */
async function condividiSistema(c: ContenutoCondivisione): Promise<boolean> {
  const file = new File([c.blob], c.nomeFile, { type: "image/png" });
  try {
    if (puoCondividereFile(file)) {
      await navigator.share({ files: [file], title: c.titolo, text: c.testo });
      return true;
    }
    if (typeof navigator.share === "function" && /Android|iPhone|iPad/.test(navigator.userAgent)) {
      await navigator.share({ title: c.titolo, text: c.testo });
      return true;
    }
  } catch (e) {
    // l'utente ha chiuso il foglio: non e' un errore
    if ((e as { name?: string }).name === "AbortError") return true;
  }
  return false;
}

export function BottoneCondividi({
  genera,
  etichetta = tr("Condividi"),
  className = "btn-ghost w-full",
  disabled,
  title,
}: {
  /** costruisce immagine + testo al momento del click */
  genera: () => Promise<ContenutoCondivisione>;
  etichetta?: string;
  className?: string;
  disabled?: boolean;
  title?: string;
}) {
  const [inCorso, setInCorso] = useState(false);
  const [finestra, setFinestra] = useState<ContenutoCondivisione | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  const avvia = async () => {
    setInCorso(true);
    setErrore(null);
    try {
      const c = await genera();
      if (!(await condividiSistema(c))) setFinestra(c);
    } catch (e) {
      setErrore(e instanceof Error ? e.message : tr("Non sono riuscito a preparare l'immagine"));
    } finally {
      setInCorso(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => void avvia()}
        disabled={disabled || inCorso}
        className={`${className} disabled:opacity-50`}
        title={title}
      >
        {inCorso ? tr("Preparo l'immagine…") : tr("📤 {{v1}}", { v1: etichetta })}
      </button>
      {errore && <div className="text-xs text-danger">{errore}</div>}
      {finestra && <FinestraCondividi c={finestra} onClose={() => setFinestra(null)} />}
    </>
  );
}

function FinestraCondividi({ c, onClose }: { c: ContenutoCondivisione; onClose: () => void }) {
  const [url] = useState(() => URL.createObjectURL(c.blob));
  const [copiato, setCopiato] = useState(false);
  const testoUrl = encodeURIComponent(c.testo);

  const copia = async () => {
    try {
      await navigator.clipboard.writeText(c.testo);
      setCopiato(true);
      setTimeout(() => setCopiato(false), 1500);
    } catch {
      /* niente appunti */
    }
  };

  const chiudi = () => {
    URL.revokeObjectURL(url);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4"
      onClick={chiudi}
    >
      <div
        className="bg-bg-card border border-accent/30 sm:rounded-2xl rounded-t-2xl w-full max-w-md max-h-[94vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-primary/30">
          <h3 className="font-display text-lg text-fg">📤 {c.titolo}</h3>
          <button
            type="button"
            onClick={chiudi}
            className="w-9 h-9 rounded-full border border-primary/40 text-fg-muted hover:text-fg"
            aria-label={tr("Chiudi")}
          >
            ✕
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <img
            src={url}
            alt={c.titolo}
            className="w-full max-h-[50vh] object-contain rounded-xl border border-primary/30 bg-bg"
          />
          <div className="grid grid-cols-2 gap-2">
            <a href={url} download={c.nomeFile} className="btn-primary text-sm">
              {tr("⬇️ Salva immagine")}
            </a>
            <button type="button" onClick={() => void copia()} className="btn-ghost text-sm">
              {copiato ? tr("✓ Copiato") : tr("📋 Copia testo")}
            </button>
            <a
              href={`https://wa.me/?text=${testoUrl}`}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost text-sm"
            >
              WhatsApp
            </a>
            <a
              href={`https://t.me/share/url?url=${encodeURIComponent("https://fantasquad.it")}&text=${testoUrl}`}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost text-sm"
            >
              Telegram
            </a>
          </div>
          <p className="text-[11px] text-fg-muted">
            {tr(
              tr(
                "WhatsApp e Telegram da qui mandano il testo: per allegare l'immagine salvala e caricala nella chat. Per Instagram salva l'immagine e pubblicala come storia o post. Dal telefono, invece, l'immagine parte direttamente nell'app che scegli.",
              ),
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
